<template>
  <div class="organization-positions-table-wrapper">
    <UniversalTable
      :config="tableConfig.tableParams.value"
      :items="mappedPositions"
      :items-count="backendPagination ? pagination.total : totalFilteredCount"
      :backend-pagination="true"
      :externalCurrentPage="
        backendPagination ? pagination.page : viewState.state.pagination.page
      "
      :loading="loading"
      :emptyObjectData="{
        singular: 'Position',
        plural: 'Positions',
        emptyTitle: 'No positions created',
        emptyMessage:
          'In an Organization you need Positions to be filled by Employees',
        emptyButtonLabel: 'Create Position',
      }"
      @onActionClick="handleAddPosition"
      @onSearch="handleSearch"
      @onClick="handleFilterClick"
      @removeFilter="handleRemoveFilter"
      @removeAllFilters="handleClearFilters"
      @onRequest="handlePageRequest"
      @onSort="handleSort"
      @updatePositionsViewSelection="handleUpdatePositionsViewSelection"
    >
      <template #Dropdown-activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </UniversalTable>

    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="handleApplyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Positions"
      :filterGroups="viewState.state.filters.filterGroups"
      :groupsItems="viewState.state.filters.groupsItems"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="620"
      :defaultSelected="viewState.state.filters.values"
    />

    <!-- Add Position Modal -->
    <AddOrgObject
      :show="viewState.state.addPositionModal.show"
      @onAdd="
        handlePositionOperation(positions.operations.CREATE_POSITION, $event)
      "
      OrgObjectType="Position"
      ghostText="+ Add"
      description="You are about to add a new"
      type="add"
      :button-label="isAppLoading ? 'Saving' : 'Save'"
      @close="viewState.state.addPositionModal.show = false"
    />

    <!-- Activate Position Modal -->
    <ExOrgObjectActions
      v-model="viewState.state.activatePositionModal.show"
      objectType="Position"
      :name="currentPositionName"
      @onAction="
        handlePositionOperation(positions.operations.ACTIVATE_POSITION)
      "
    />

    <!-- Delete Position Modal -->
    <ExDeleteOrgObject
      v-model="viewState.state.deletePositionModal.show"
      OrgObjectType="Position"
      :name="currentPositionName"
      @onAction="handlePositionOperation(positions.operations.DELETE_POSITION)"
    />

    <!-- Rename Position Modal -->
    <RenameOrgObject
      v-if="viewState.state.renamePositionModal.show"
      :openDialog="viewState.state.renamePositionModal.show"
      :name="currentPositionName"
      OrgObjectType="Position"
      :placeholder="currentPositionName"
      @onAction="
        handlePositionOperation(positions.operations.RENAME_POSITION, $event)
      "
      @onClose="viewState.state.renamePositionModal.show = false"
      :isShowCharCounter="true"
      :maxWidth="'430'"
    />

    <!-- Archive/Unarchive Position Modal -->
    <ArchivePopUp
      :action="viewState.state.archiveAction"
      type="position"
      :show="viewState.state.archivePositionModal.show"
      :curretObj="positions.getCurrentPosition()"
      @onClose="viewState.state.archivePositionModal.show = false"
      @onArchive="
        handlePositionOperation(positions.operations.ARCHIVE_POSITION)
      "
    />

    <!-- Attach Role Modal -->
    <AttachedRole
      v-model="viewState.state.attachRoleModal.show"
      OrgObjectType="Role to Position"
      @onSave="viewState.attachRole(accountID)"
      :items="viewState.state.attachRoleModal.roles"
      @item-selected="viewState.handleRoleSelected"
      @add-item-selected="viewState.handleAddRoleSelected"
      :parent-name="currentPositionName"
      :existingNamesList="[]"
      :openDefault="viewState.state.openDefault"
      :isLoadingMore="viewState.state.attachRoleModal.isLoadingMore"
      :hasMoreData="viewState.state.attachRoleModal.hasMoreData"
      @load-more="viewState.loadMoreRoles"
      @search="viewState.searchRoles"
      :type="
        showAttachResponsibilitiesPicker
          ? 'attach_role_to_position_new_picker'
          : 'attach_role_to_position'
      "
    />

    <!-- Relieve Primary Assignment Modal -->
    <RelievePrimaryAssignment
      v-if="viewState.state.relieveAssignment.show"
      :show="viewState.state.relieveAssignment.show"
      :type="viewState.state.relieveAssignment.type"
      :avatar="viewState.getRelieveAssignmentAvatar()"
      :current-position="currentPositionName"
      @onClose="viewState.closeRelieveAssignment()"
      @onAction="viewState.relievePrimaryAssignment(accountID)"
    />

    <!-- Retire Position Modal -->
    <RetirePositionPopup
      v-model="viewState.state.retirePositionModal.show"
      OrgObjectType="Position"
      :name="currentPositionName"
      @onAction="handlePositionOperation(positions.operations.RETIRE_POSITION)"
    />

    <!-- Reactivate Position Modal -->
    <ReActivatePosition
      v-model="viewState.state.reactivatePositionModal.show"
      OrgObjectType="Position"
      :name="currentPositionName"
      @onAction="
        handlePositionOperation(positions.operations.REACTIVATE_POSITION)
      "
    />

    <!-- Deactivate Position Modal -->
    <ExDeActivatePopup
      v-model="viewState.state.deactivatePositionModal.show"
      OrgObjectType="Position"
      :name="currentPositionName"
      @onAction="
        handlePositionOperation(positions.operations.DEACTIVATE_POSITION)
      "
    />

    <!-- Clone Position Modal -->
    <ExCloneOrgObject
      v-model="viewState.state.clonePositionModal.show"
      OrgObjectType="Position"
      :name="currentPositionName"
      @onAction="handlePositionOperation(positions.operations.CLONE_POSITION)"
    />

    <!-- Chatter Drawer -->
    <ChecklistDrawer
      ref="drawerContainer"
      :type="'Position'"
      :drawer="chatterModalShow"
      :typeValue="currentPositionName"
      @closeDrawer="viewState.closePositionChatterModal()"
      :chatterList="chatterModalData"
      :totalCheckpointChatter="chatterModalTotal"
      iconName="org_icon_position"
      @requestMoreCheckpointsChatter="viewState.requestMoreChatter()"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref, getCurrentInstance } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import AddOrgObject from "@components/AddOrgObject/AddOrgObject.vue";
import ExOrgObjectActions from "@components/common/ExOrgObjectActions.vue";
import ExDeleteOrgObject from "@components/DeleteOrgObject/ExDeleteOrgObject.vue";
import RenameOrgObject from "@components/RenameOrgObject/RenameOrgObject.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import AttachedRole from "@components/RolesCatalog/AttachedRole.vue";
import RelievePrimaryAssignment from "@components/organizations/employees/RelievePrimaryAssignment.vue";
import RetirePositionPopup from "@components/RetirePositionPopup/RetirePositionPopup.vue";
import ReActivatePosition from "@components/ReActivatePosition/ReActivatePositionPopup.vue";
import ExDeActivatePopup from "@components/DeActivatePosition/DeActivatePositionPopup.vue";
import ExCloneOrgObject from "@components/CloneOrgObject/ExCloneOrgObject.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import { useTableConfig, setEventCallbacks } from "./useTableConfig";
import { useOrganizationPositionsViewState } from "./useViewState";

const app = getCurrentInstance();
const { proxy } = app;

const viewState = useOrganizationPositionsViewState();
const positions = viewState.positions;

const accountID = ref(JSON.parse(localStorage.getItem("currentAccountID")));
const drawerContainer = ref(null);

const isAppLoading = computed(() => proxy?.$loader?.isAppLoading || false);
const currentPositionName = computed(
  () => positions.getCurrentPosition()?.name || ""
);
const chatterModalShow = computed(
  () => viewState.state.openChatterModal?.show || false
);
const chatterModalData = computed(
  () => viewState.state.openChatterModal?.chatterData || []
);
const chatterModalTotal = computed(
  () => viewState.state.openChatterModal?.totalPositionChatter || 0
);

const props = defineProps({
  positions: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  searchText: {
    type: String,
    default: "",
  },
  backendPagination: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Positions Organization",
  },
  actionLabel: {
    type: String,
    default: "Add Position",
  },
  hideTopSection: {
    type: Boolean,
    default: true,
  },
  hideActiveNo: {
    type: Boolean,
    default: false,
  },
  activeRecordsCount: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  "addPosition",
  "positionClick",
  "closeClick",
  "preparingAvatarsClick",
  "preparingCloseClick",
  "preparingEffectiveDateClick",
  "rowChatterClick",
  "menuClick",
  "search",
  "filterClick",
  "removeFilter",
  "pageChange",
  "sort",
]);

const tableConfig = useTableConfig(viewState);

// Set up event callbacks
setEventCallbacks({
  onCloseClick: (item) => {
    // Set current position and show relieve assignment modal
    positions.setCurrentPosition(item);
    viewState.state.relieveAssignment.show = true;
    viewState.state.relieveAssignment.type = "primary";
    viewState.state.relieveAssignment.assignedEmployee =
      item.assigned_primary || item.occupiedBy;
    viewState.state.relieveAssignment.currentPosition = item;
    emit("closeClick", item);
  },
  onPreparingCloseClick: (item) => {
    // Set current position and show relieve preparing assignment modal
    positions.setCurrentPosition(item);
    viewState.state.relieveAssignment.show = true;
    viewState.state.relieveAssignment.type = "prepare";
    const preparingEmployee =
      item.assigned_preparing_for?.[0] ||
      item.preparingFor?.preparingEmployees?.[0];
    viewState.state.relieveAssignment.assignedEmployee = preparingEmployee;
    viewState.state.relieveAssignment.currentPosition = item;
    emit("preparingCloseClick", item);
  },
  onPreparingAvatarsClick: (item) => emit("preparingAvatarsClick", item),
  onPreparingEffectiveDateClick: (event, item) =>
    emit("preparingEffectiveDateClick", event, item),
  onRowChatterClick: (item) => {
    // Open chatter modal
    positions.setCurrentPosition(item);
    viewState.openPositionChatterModal(item);
    emit("rowChatterClick", item);
  },
  onMenuClick: async ({ item, operation }) => {
    // Handle dropdown menu operations
    positions.setCurrentPosition(item);

    switch (operation?.value) {
      case "activate_action":
        viewState.state.activatePositionModal.show = true;
        break;
      case "delete_action":
        viewState.state.deletePositionModal.show = true;
        break;
      case "rename_action":
        viewState.state.renamePositionModal.show = true;
        break;
      case "attach_position_role":
        viewState.state.openDefault = true;
        await viewState.getListOfRoles(item.id, 1, "", false);
        viewState.state.attachRoleModal.show = true;
        break;
      case "archive_action":
        viewState.state.archiveAction = "archive";
        viewState.state.archivePositionModal.show = true;
        break;
      case "unarchive_action":
        viewState.state.archiveAction = "unarchive";
        viewState.state.archivePositionModal.show = true;
        break;
      case "retire_action":
        viewState.state.retirePositionModal.show = true;
        break;
      case "reactivate_action":
        viewState.state.reactivatePositionModal.show = true;
        break;
      case "deactivate_action":
        viewState.state.deactivatePositionModal.show = true;
        break;
      case "cloned_action":
        viewState.state.clonePositionModal.show = true;
        break;
    }

    emit("menuClick", { item, operation });
  },
  onPositionClick: (item) => emit("positionClick", item),
});

// Update viewState config from props
viewState.config.title = props.title;
viewState.config.actionLabel = props.actionLabel;
viewState.config.hideTopSection = props.hideTopSection;
viewState.config.hideActiveNo = props.hideActiveNo;
viewState.config.activeRecordsCount = props.activeRecordsCount;

// Sync filters and search from props
watch(
  () => props.appliedFilters,
  (newFilters) => {
    if (newFilters && Array.isArray(newFilters)) {
      viewState.state.filters.values = [...newFilters];
    }
  },
  { immediate: true }
);

watch(
  () => props.searchText,
  (newSearchText) => {
    if (newSearchText !== undefined) {
      viewState.state.filters.searchText = newSearchText;
    }
  },
  { immediate: true }
);

// Backend handles all filtering, searching, and sorting - just pass positions through
const filteredAndSortedPositions = computed(() => {
  return [...props.positions];
});

const showAttachResponsibilitiesPicker = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP389"];
});

// Total count for pagination
const totalFilteredCount = computed(() => {
  return filteredAndSortedPositions.value.length;
});

// Paginated positions (10 per page) - slice for current page when doing frontend pagination
const paginatedPositions = computed(() => {
  if (props.backendPagination) {
    // Backend pagination - pass all items as-is
    return filteredAndSortedPositions.value;
  }

  // Frontend pagination - slice items for current page
  const currentPage = viewState.state.pagination.page || 1;
  const itemsPerPage = props.pagination.perPage || 10;
  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredAndSortedPositions.value.slice(start, end);
});

// Map positions data with IDs
const mappedPositions = computed(() => {
  return paginatedPositions.value.map((item, index) => ({
    id: item.id || `position-${index}-${Math.random()}`,
    ...item,
  }));
});

// Event handlers
const handleAddPosition = () => {
  viewState.state.addPositionModal.show = true;
  emit("addPosition");
};

const handleFilterClick = () => {
  viewState.state.filters.showFilterDialog = true;
  emit("filterClick");
};

const handlePageRequest = async (request) => {
  if (props.backendPagination) {
    positions.setPaginationDataLimit(request.itemsPerPage || 10);
    positions.setPaginationDataOffset(
      (request.page - 1) * (request.itemsPerPage || 10)
    );
    await positions.loadAllPositions();
    emit("pageChange", request.page);
  } else {
    // Frontend pagination - update viewState and UniversalTable will update via externalCurrentPage
    viewState.handleRequest(request);
  }
};

const handleSort = async (sortData) => {
  if (props.backendPagination) {
    const finalSortField = sortData.descending
      ? `-${sortData.field}`
      : `${sortData.field}`;
    positions.setOrderingDataField(finalSortField);
    await positions.loadAllPositions();
    emit("sort", sortData);
  } else {
    // Frontend sorting
    viewState.handleSort(sortData);
  }
};

const handleSearch = async (searchText) => {
  positions.removePositionFilter("name");
  if (searchText && searchText.trim() !== "") {
    positions.addPositionFilter("name", searchText);
  }
  positions.setPaginationDataOffset(0);
  viewState.state.filters.searchText = searchText;
  await positions.loadAllPositions();
  emit("search", searchText);
};

const handleApplyFilters = async (filters) => {
  const newFilters = filters
    .map((item) => {
      if (item.value !== null) {
        return typeof item === "string" ? item : item.value;
      }
      return null;
    })
    .filter(Boolean);

  viewState.state.filters.values = newFilters;
  positions.clearPositionDynamicFilters();

  // Collect all unique status filters first to avoid duplicates
  const statusOptions = ["draft", "active", "retired", "inactive"];
  const statusFilters = [
    ...new Set(
      newFilters
        .filter((el) => {
          const lowerEl = typeof el === "string" ? el.toLowerCase() : el;
          return statusOptions.includes(lowerEl);
        })
        .map((el) => (typeof el === "string" ? el.toLowerCase() : el))
    ),
  ];

  // Apply all status filters
  statusFilters.forEach((status) => {
    positions.addPositionFilter("status", status);
  });

  // Handle archived filter
  let archivedFilterApplied = false;
  newFilters.forEach((el) => {
    if (el === "Show" || el === "show_archived") {
      archivedFilterApplied = true;
      positions.addPositionFilter("archived", true);
    } else if (el === "Do not Show" || el === "no_archived") {
      archivedFilterApplied = true;
      positions.addPositionFilter("archived", false);
    }
  });

  if (!archivedFilterApplied) {
    positions.addPositionFilter("archived", false);
  }

  viewState.state.filters.showFilterDialog = false;
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const handleRemoveFilter = async (removedFilter) => {
  viewState.removeFilters(removedFilter);
  await handleApplyFilters(
    viewState.state.filters.values.map((value) =>
      typeof value === "string" ? { label: value, value: value } : value
    )
  );
};

const handleClearFilters = async () => {
  viewState.state.filters.values = [];
  positions.clearPositionDynamicFilters();
  positions.addPositionFilter("archived", false);
  positions.setPaginationDataOffset(0);
  await positions.loadAllPositions();
};

const handleUpdatePositionsViewSelection = (selection) => {
  viewState.config.positionsViewSelection = selection;
};

// Position operation handler - same as PositionView.vue
const handlePositionOperation = async (operation, payload = null) => {
  await viewState.handlePositionOperation(operation, accountID.value, payload);
};

onMounted(async () => {
  await tableConfig.initializeDropdownFlags();

  // Skip loading persisted filters in Storybook - use props instead
  const isStorybook =
    typeof window !== "undefined" &&
    (window.location?.href?.includes("localhost:6006") ||
      window.location?.href?.includes("storybook"));

  if (!isStorybook) {
    viewState.getFilters();
  }
});
</script>

<style scoped>
.organization-positions-table-wrapper .container {
  padding: 0px !important;
}
</style>
